import { useEffect, useState, useCallback, useRef } from "react";
import { opApi, apiError } from "@/lib/api";
import { money } from "@/components/mc/ui";
import { QRCodeCanvas } from "qrcode.react";
import { buildJoinUrl, downloadCanvasPng, getCanvasDataUrl, printLoyaltyCard, downloadLoyaltyCardPdf } from "@/components/mc/marketingUtils";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { Switch } from "@/components/ui/switch";
import { Progress } from "@/components/ui/progress";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription, DialogFooter } from "@/components/ui/dialog";
import { toast } from "sonner";
import { Plus, Pencil, Trash2, Users, Gift, Award, Calendar, X, Printer, Download, QrCode } from "lucide-react";
import { AddPointsByPhone } from "@/components/mc/AddPointsByPhone";

const EMPTY = {
  title: "", description: "", earn_type: "points", earn_rate: 1, goal: 20, reward: "",
  tiers: [{ goal: 20, reward: "", reward_type: "percent", reward_value: 10 }],
  enrollment: "auto", renewable: true, starts_at: "", ends_at: "", accent: "#0033A0", active: true,
};

const REWARD_TYPES = [
  { value: "percent", label: "% off order" },
  { value: "fixed", label: "$ off order" },
  { value: "free_item", label: "Free item (cheapest in cart)" },
  { value: "bogo", label: "Buy one, get one free" },
  { value: "custom", label: "Custom (no auto-discount)" },
];

const autoLabel = (t) => {
  const v = t.reward_value;
  if (t.reward_type === "percent") return v ? `${v}% off` : "% off";
  if (t.reward_type === "fixed") return v ? `$${v} off` : "$ off";
  if (t.reward_type === "free_item") return "Free item";
  if (t.reward_type === "bogo") return "Buy one, get one free";
  return "";
};

const normTiers = (p) => {
  if (Array.isArray(p.tiers) && p.tiers.length) return p.tiers.map((t) => ({
    goal: t.goal, reward: t.reward || "",
    reward_type: t.reward_type || "custom",
    reward_value: t.reward_value ?? "",
  }));
  return [{ goal: p.goal || 20, reward: p.reward || "", reward_type: "custom", reward_value: "" }];
};

const toDateInput = (iso) => (iso ? String(iso).slice(0, 10) : "");
const fromDateInput = (v) => (v ? new Date(v + "T00:00:00Z").toISOString() : null);

function ProgramCard({ p, onEdit, onDelete, onMembers, tenant }) {
  const earn = p.earn_type === "stamps" ? `${p.earn_rate} stamp / order` : `${p.earn_rate} pt / $1`;
  const qrRef = useRef(null);
  const slug = tenant?.slug || "";
  const joinUrl = buildJoinUrl(slug, p.id);
  const logoAbs = tenant?.logo_url
    ? (tenant.logo_url.startsWith("/") ? (process.env.REACT_APP_BACKEND_URL + tenant.logo_url) : tenant.logo_url)
    : "";
  const cardOpts = () => ({ shopName: tenant?.name || "", logoUrl: logoAbs, program: p, qrDataUrl: getCanvasDataUrl(qrRef.current) });
  const doPdf = async () => { try { await downloadLoyaltyCardPdf(cardOpts()); } catch (e) { toast.error("Could not generate PDF"); } };
  return (
    <div className="relative bg-card border border-border overflow-hidden flex flex-col" data-testid={`program-card-${p.id}`}>
      <div className="h-1.5 w-full" style={{ backgroundColor: p.accent || "#0033A0" }} />
      <div className="p-5 flex-1 flex flex-col">
        <div className="flex items-start justify-between gap-2">
          <div className="min-w-0">
            <div className="font-display font-bold text-lg leading-tight truncate">{p.title}</div>
            <div className="text-xs text-muted-foreground uppercase tracking-wider mt-0.5">
              {p.enrollment === "auto" ? "Auto-enroll" : "Opt-in"} · {p.renewable ? "Renewable" : "One-time"}
              {!p.active && " · Paused"}
            </div>
          </div>
          <div className="flex items-center gap-1 shrink-0">
            <button onClick={() => onMembers(p)} className="text-muted-foreground hover:text-foreground" title="Members" data-testid={`program-members-${p.id}`}><Users className="h-4 w-4" /></button>
            <button onClick={() => onEdit(p)} className="text-muted-foreground hover:text-foreground" title="Edit" data-testid={`program-edit-${p.id}`}><Pencil className="h-4 w-4" /></button>
            <button onClick={() => onDelete(p)} className="text-muted-foreground hover:text-accent" title="Delete" data-testid={`program-delete-${p.id}`}><Trash2 className="h-4 w-4" /></button>
          </div>
        </div>

        <div className="mt-4 space-y-1.5" data-testid={`program-tiers-${p.id}`}>
          {normTiers(p).map((t, i) => (
            <div key={i} className="flex items-center gap-2">
              <span className="font-mono font-bold text-lg w-10 text-right shrink-0" style={{ color: p.accent || "#0033A0" }}>{t.goal}</span>
              <span className="text-[10px] text-muted-foreground uppercase shrink-0 w-10">{p.earn_type === "stamps" ? "stamps" : "pts"}</span>
              <Gift className="h-3.5 w-3.5 shrink-0" style={{ color: p.accent || "#0033A0" }} />
              <span className="font-semibold text-sm truncate">{t.reward || "—"}</span>
            </div>
          ))}
        </div>

        <div className="mt-3 text-xs text-muted-foreground flex items-center gap-1"><Award className="h-3.5 w-3.5" /> {earn}</div>
        {(p.starts_at || p.ends_at) ? (
          <div className="mt-1 text-xs text-muted-foreground flex items-center gap-1">
            <Calendar className="h-3.5 w-3.5" /> {toDateInput(p.starts_at) || "—"} → {toDateInput(p.ends_at) || "—"}
          </div>
        ) : (
          <div className="mt-1 text-xs text-[hsl(var(--success))] flex items-center gap-1"><Calendar className="h-3.5 w-3.5" /> No expiry (perpetual)</div>
        )}
        {p.description && <p className="mt-3 text-xs text-muted-foreground border-t border-border pt-3 line-clamp-3">{p.description}</p>}

        <div className="mt-3 pt-3 border-t border-border flex items-center gap-3">
          <div className="bg-white p-1.5 border border-border shrink-0" ref={qrRef} title="Sign-up QR (auto-enrolls into this offer)">
            <QRCodeCanvas value={joinUrl} size={72} level="M" fgColor={p.accent || "#111111"} data-testid={`program-qr-${p.id}`} />
          </div>
          <div className="min-w-0 flex-1">
            <div className="text-[11px] text-muted-foreground mb-1.5">Scan to opt in to this offer</div>
            <div className="flex flex-wrap gap-1.5">
              <Button variant="outline" size="sm" className="rounded-none h-7 gap-1 text-xs px-2" onClick={() => printLoyaltyCard(cardOpts())} data-testid={`program-print-${p.id}`}>
                <Printer className="h-3 w-3" /> Print
              </Button>
              <Button variant="outline" size="sm" className="rounded-none h-7 gap-1 text-xs px-2" onClick={doPdf} data-testid={`program-pdf-${p.id}`}>
                <Download className="h-3 w-3" /> PDF
              </Button>
              <Button variant="outline" size="sm" className="rounded-none h-7 gap-1 text-xs px-2" onClick={() => downloadCanvasPng(qrRef.current, `${p.title || "offer"}-qr`)} data-testid={`program-qrpng-${p.id}`}>
                <QrCode className="h-3 w-3" /> QR
              </Button>
            </div>
          </div>
        </div>
        <div className="mt-3 pt-3 border-t border-border text-xs text-muted-foreground">{p.member_count || 0} member{p.member_count === 1 ? "" : "s"}</div>
      </div>
    </div>
  );
}

function MembersDialog({ program, open, onOpenChange }) {
  const [members, setMembers] = useState([]);
  const [editId, setEditId] = useState(null);
  const [editVal, setEditVal] = useState("");

  const load = useCallback(async () => {
    if (!program) return;
    try { const { data } = await opApi.get(`/tenant/loyalty/programs/${program.id}/members`); setMembers(data); }
    catch (e) { toast.error(apiError(e.response?.data?.detail)); }
  }, [program]);

  useEffect(() => { if (open) load(); }, [open, load]);

  const redeem = async (m, index) => {
    try { await opApi.post(`/tenant/loyalty/memberships/${m.id}/redeem`, index != null ? { reward_index: index } : {}); toast.success("Reward redeemed"); load(); }
    catch (e) { toast.error(apiError(e.response?.data?.detail)); }
  };
  const saveEdit = async (m) => {
    const v = parseFloat(editVal);
    if (isNaN(v)) { setEditId(null); return; }
    try { await opApi.put(`/tenant/loyalty/memberships/${m.id}`, { progress: Math.max(0, v) }); setEditId(null); load(); }
    catch (e) { toast.error(apiError(e.response?.data?.detail)); }
  };

  if (!program) return null;
  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="rounded-none max-w-lg">
        <DialogHeader>
          <DialogTitle className="font-display">{program.title} — members</DialogTitle>
          <DialogDescription>Goal: {program.goal} {program.earn_type === "stamps" ? "stamps" : "points"} → {program.reward}. Edit a member's balance or redeem a ready reward.</DialogDescription>
        </DialogHeader>
        <div className="max-h-[60vh] overflow-y-auto border-t border-l border-border" data-testid="members-list">
          {members.map((m) => (
            <div key={m.id} className="border-r border-b border-border p-3 flex items-center gap-3" data-testid={`member-${m.id}`}>
              <div className="flex-1 min-w-0">
                <div className="font-medium text-sm truncate">{m.customer_name || "—"}</div>
                <div className="text-xs text-muted-foreground truncate">{m.customer_email}</div>
                <div className="mt-1.5 flex items-center gap-2">
                  {editId === m.id ? (
                    <>
                      <Input className="rounded-none h-7 w-20 text-xs" value={editVal} onChange={(e) => setEditVal(e.target.value)} data-testid={`member-edit-input-${m.id}`} />
                      <Button size="sm" className="rounded-none h-7 text-xs" onClick={() => saveEdit(m)} data-testid={`member-edit-save-${m.id}`}>Save</Button>
                    </>
                  ) : (
                    <>
                      <div className="flex-1 max-w-[140px]"><Progress value={Math.min(100, (m.progress / Math.max(1, program.goal)) * 100)} className="h-1.5" /></div>
                      <span className="font-mono text-xs">{m.progress}/{program.goal}</span>
                      <button className="text-muted-foreground hover:text-foreground" onClick={() => { setEditId(m.id); setEditVal(String(m.progress)); }} data-testid={`member-edit-${m.id}`}><Pencil className="h-3.5 w-3.5" /></button>
                    </>
                  )}
                </div>
              </div>
              <div className="text-right shrink-0">
                {(m.ready_rewards || []).length > 0 ? (
                  <div className="flex flex-col items-end gap-1" data-testid={`member-rewards-${m.id}`}>
                    {m.ready_rewards.map((rr, idx) => (
                      <Button key={idx} size="sm" className="rounded-none h-8 text-xs gap-1" onClick={() => redeem(m, idx)} data-testid={`member-redeem-${m.id}-${idx}`}><Gift className="h-3.5 w-3.5" /> {rr.reward || "reward"}</Button>
                    ))}
                  </div>
                ) : m.rewards_ready > 0 ? (
                  <Button size="sm" className="rounded-none h-8 text-xs gap-1" onClick={() => redeem(m)} data-testid={`member-redeem-${m.id}`}><Gift className="h-3.5 w-3.5" /> Redeem ({m.rewards_ready})</Button>
                ) : (
                  <span className="text-[11px] text-muted-foreground">{m.status === "claimed" ? "Claimed" : `${m.rewards_redeemed || 0} redeemed`}</span>
                )}
              </div>
            </div>
          ))}
          {!members.length && <div className="border-r border-b border-border p-4 text-sm text-muted-foreground">No members yet.</div>}
        </div>
      </DialogContent>
    </Dialog>
  );
}

export function LoyaltyManager({ tenant }) {
  const [programs, setPrograms] = useState([]);
  const [form, setForm] = useState(EMPTY);
  const [editId, setEditId] = useState(null);
  const [open, setOpen] = useState(false);
  const [membersFor, setMembersFor] = useState(null);
  const [membersOpen, setMembersOpen] = useState(false);

  const load = useCallback(async () => {
    try { const { data } = await opApi.get("/tenant/loyalty/programs"); setPrograms(data); }
    catch (e) { /* guarded */ }
  }, []);
  useEffect(() => { load(); }, [load]);

  const openNew = () => { setEditId(null); setForm(EMPTY); setOpen(true); };
  const openEdit = (p) => {
    setEditId(p.id);
    setForm({ ...EMPTY, ...p, tiers: normTiers(p), starts_at: toDateInput(p.starts_at), ends_at: toDateInput(p.ends_at) });
    setOpen(true);
  };
  const setTier = (i, patch) => setForm((f) => ({ ...f, tiers: f.tiers.map((t, idx) => idx === i ? { ...t, ...patch } : t) }));
  const addTier = () => setForm((f) => f.tiers.length >= 3 ? f : ({ ...f, tiers: [...f.tiers, { goal: "", reward: "", reward_type: "percent", reward_value: 10 }] }));
  const removeTier = (i) => setForm((f) => f.tiers.length <= 1 ? f : ({ ...f, tiers: f.tiers.filter((_, idx) => idx !== i) }));
  const save = async () => {
    const tiers = (form.tiers || []).map((t) => {
      const goal = parseInt(t.goal, 10) || 0;
      const rt = t.reward_type || "custom";
      const out = { goal, reward: ((t.reward || "").trim() || autoLabel(t)) };
      if (rt !== "custom") {
        out.reward_type = rt;
        out.reward_value = (rt === "percent" || rt === "fixed") ? (parseFloat(t.reward_value) || 0) : 0;
      } else {
        out.reward_type = "custom";
        out.reward_value = 0;
      }
      return out;
    }).filter((t) => t.goal > 0);
    if (!tiers.length) { toast.error("Add at least one tier with a goal."); return; }
    tiers.sort((a, b) => a.goal - b.goal);
    const top = tiers[tiers.length - 1];
    const payload = {
      ...form,
      earn_rate: parseFloat(form.earn_rate) || 1,
      tiers,
      goal: top.goal,
      reward: top.reward,
      starts_at: fromDateInput(form.starts_at),
      ends_at: fromDateInput(form.ends_at),
    };
    try {
      if (editId) await opApi.put(`/tenant/loyalty/programs/${editId}`, payload);
      else await opApi.post("/tenant/loyalty/programs", payload);
      toast.success("Program saved"); setOpen(false); load();
    } catch (e) { toast.error(apiError(e.response?.data?.detail)); }
  };
  const del = async (p) => {
    if (!window.confirm(`Delete "${p.title}"? Members and their progress will be removed.`)) return;
    try { await opApi.delete(`/tenant/loyalty/programs/${p.id}`); load(); }
    catch (e) { toast.error(apiError(e.response?.data?.detail)); }
  };
  const showMembers = (p) => { setMembersFor(p); setMembersOpen(true); };

  const active = programs.filter((p) => !p.expired);
  const expired = programs.filter((p) => p.expired);

  const Section = ({ title, items, testid }) => (
    <div className="mb-8" data-testid={testid}>
      <h3 className="font-display font-bold text-sm uppercase tracking-wider text-muted-foreground mb-3">{title} <span className="font-mono">({items.length})</span></h3>
      {items.length ? (
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
          {items.map((p) => <ProgramCard key={p.id} p={p} onEdit={openEdit} onDelete={del} onMembers={showMembers} tenant={tenant} />)}
        </div>
      ) : (
        <div className="text-sm text-muted-foreground border border-dashed border-border p-4">No {title.toLowerCase()}.</div>
      )}
    </div>
  );

  return (
    <div>
      <div className="flex justify-between items-center mb-6">
        <div>
          <h2 className="font-display font-bold text-xl">Loyalty & promos</h2>
          <p className="text-sm text-muted-foreground mt-1">Build reward programs. Customers earn points or stamps and redeem rewards.</p>
        </div>
        <Button className="rounded-none gap-2" onClick={openNew} data-testid="add-program-btn"><Plus className="h-4 w-4" /> New program</Button>
      </div>

      <AddPointsByPhone />
      <Section title="Active offers" items={active} testid="active-offers" />
      <Section title="Expired offers" items={expired} testid="expired-offers" />

      <Dialog open={open} onOpenChange={setOpen}>
        <DialogContent className="rounded-none max-w-lg max-h-[90vh] overflow-y-auto">
          <DialogHeader>
            <DialogTitle className="font-display">{editId ? "Edit" : "New"} program</DialogTitle>
            <DialogDescription>Set how customers earn, what they win, and when it's valid. Edit the details/terms freely — like a business card.</DialogDescription>
          </DialogHeader>
          <div className="space-y-3">
            <div><Label>Title</Label><Input className="rounded-none mt-1" value={form.title} onChange={(e) => setForm({ ...form, title: e.target.value })} placeholder="e.g. Coffee Club" data-testid="program-title-input" /></div>
            <div className="grid grid-cols-2 gap-3">
              <div>
                <Label>Earn type</Label>
                <select className="mt-1 w-full h-9 rounded-none border border-input bg-background px-2 text-sm" value={form.earn_type} onChange={(e) => setForm({ ...form, earn_type: e.target.value })} data-testid="program-earntype-select">
                  <option value="points">Points per $ spent</option>
                  <option value="stamps">Stamps per order</option>
                </select>
              </div>
              <div><Label>{form.earn_type === "stamps" ? "Stamps / order" : "Points / $1"}</Label><Input type="number" step="0.1" className="rounded-none mt-1" value={form.earn_rate} onChange={(e) => setForm({ ...form, earn_rate: e.target.value })} data-testid="program-rate-input" /></div>
            </div>
            <div className="border border-border p-3" data-testid="program-tiers-editor">
              <div className="flex items-center justify-between mb-2">
                <Label>Reward tiers <span className="text-muted-foreground normal-case">(up to 3 · reached in order)</span></Label>
                {form.tiers.length < 3 && <Button type="button" variant="outline" size="sm" className="rounded-none h-7 gap-1 text-xs" onClick={addTier} data-testid="program-add-tier"><Plus className="h-3 w-3" /> Add tier</Button>}
              </div>
              <div className="space-y-3">
                {form.tiers.map((t, i) => {
                  const needsValue = t.reward_type === "percent" || t.reward_type === "fixed";
                  return (
                  <div key={i} className="border border-border p-2.5 space-y-2" data-testid={`program-tier-${i}`}>
                    <div className="flex items-end gap-2">
                      <div className="w-20">
                        <div className="text-[10px] uppercase tracking-wider text-muted-foreground mb-1">{form.earn_type === "stamps" ? "Stamps" : "Points"}</div>
                        <Input type="number" className="rounded-none" value={t.goal} onChange={(e) => setTier(i, { goal: e.target.value })} placeholder="e.g. 4" data-testid={`program-tier-goal-${i}`} />
                      </div>
                      <div className="flex-1">
                        <div className="text-[10px] uppercase tracking-wider text-muted-foreground mb-1">Reward type</div>
                        <select className="w-full h-9 rounded-none border border-input bg-background px-2 text-sm" value={t.reward_type} onChange={(e) => setTier(i, { reward_type: e.target.value })} data-testid={`program-tier-type-${i}`}>
                          {REWARD_TYPES.map((rt) => <option key={rt.value} value={rt.value}>{rt.label}</option>)}
                        </select>
                      </div>
                      {needsValue && (
                        <div className="w-24">
                          <div className="text-[10px] uppercase tracking-wider text-muted-foreground mb-1">{t.reward_type === "percent" ? "Percent" : "Amount $"}</div>
                          <Input type="number" step={t.reward_type === "percent" ? "1" : "0.01"} className="rounded-none" value={t.reward_value} onChange={(e) => setTier(i, { reward_value: e.target.value })} placeholder={t.reward_type === "percent" ? "50" : "5.00"} data-testid={`program-tier-value-${i}`} />
                        </div>
                      )}
                      {form.tiers.length > 1 && <button type="button" onClick={() => removeTier(i)} className="text-muted-foreground hover:text-accent h-9 w-8 flex items-center justify-center shrink-0" data-testid={`program-tier-remove-${i}`}><Trash2 className="h-4 w-4" /></button>}
                    </div>
                    <div>
                      <div className="text-[10px] uppercase tracking-wider text-muted-foreground mb-1">Label shown to customer</div>
                      <Input className="rounded-none" value={t.reward} onChange={(e) => setTier(i, { reward: e.target.value })} placeholder={autoLabel(t) || "e.g. Free tote bag"} data-testid={`program-tier-reward-${i}`} />
                    </div>
                  </div>
                  );
                })}
              </div>
              <div className="text-[11px] text-muted-foreground mt-2">The reward type sets the actual discount applied in the cart & register (free item = cheapest item in the cart). "Custom" tracks the reward for the cashier to fulfil manually. Reaching the top tier {form.renewable ? "completes the card and starts a fresh one." : "completes the card."}</div>
            </div>
            <div className="grid grid-cols-2 gap-3">
              <div>
                <Label>Enrollment</Label>
                <select className="mt-1 w-full h-9 rounded-none border border-input bg-background px-2 text-sm" value={form.enrollment} onChange={(e) => setForm({ ...form, enrollment: e.target.value })} data-testid="program-enrollment-select">
                  <option value="auto">Auto-enroll on order</option>
                  <option value="opt_in">Customer opt-in</option>
                </select>
              </div>
              <div>
                <Label>Accent color</Label>
                <input type="color" className="mt-1 w-full h-9 border border-input bg-background cursor-pointer" value={form.accent} onChange={(e) => setForm({ ...form, accent: e.target.value })} data-testid="program-accent-input" />
              </div>
            </div>
            <div className="grid grid-cols-2 gap-3">
              <div><Label>Valid from <span className="text-muted-foreground normal-case">(optional)</span></Label><Input type="date" className="rounded-none mt-1" value={form.starts_at} onChange={(e) => setForm({ ...form, starts_at: e.target.value })} data-testid="program-starts-input" /></div>
              <div><Label>Valid until <span className="text-muted-foreground normal-case">(blank = perpetual)</span></Label><Input type="date" className="rounded-none mt-1" value={form.ends_at} onChange={(e) => setForm({ ...form, ends_at: e.target.value })} data-testid="program-ends-input" /></div>
            </div>
            <div><Label>Details / terms & conditions</Label><Textarea className="rounded-none mt-1" value={form.description} onChange={(e) => setForm({ ...form, description: e.target.value })} placeholder="e.g. One reward per visit. Cannot be combined with other offers." data-testid="program-desc-input" /></div>
            <div className="flex items-center gap-6">
              <div className="flex items-center gap-2"><Switch checked={form.renewable} onCheckedChange={(v) => setForm({ ...form, renewable: v })} data-testid="program-renewable-switch" /><Label>Renewable (auto re-enroll on reward)</Label></div>
              <div className="flex items-center gap-2"><Switch checked={form.active} onCheckedChange={(v) => setForm({ ...form, active: v })} data-testid="program-active-switch" /><Label>Active</Label></div>
            </div>
          </div>
          <DialogFooter><Button onClick={save} disabled={!form.title} className="rounded-none" data-testid="save-program-btn">Save program</Button></DialogFooter>
        </DialogContent>
      </Dialog>

      <MembersDialog program={membersFor} open={membersOpen} onOpenChange={setMembersOpen} />
    </div>
  );
}
